'use client'

import { useState } from 'react'
import Link from 'next/link'
import {
  ArrowUpRight,
  Mail,
  Send,
  Check,
  ShieldCheck,
  Newspaper,
  Handshake,
  LifeBuoy,
  Wallet,
} from 'lucide-react'
import MarketingNav from '@/components/marketing/Nav'
import MarketingFooter from '@/components/marketing/Footer'

const SUBJECTS = ['General', 'Sales', 'Support', 'Partnership'] as const
type Subject = (typeof SUBJECTS)[number]

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.pulsesignal.co'

export default function ContactForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState<Subject>('General')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)
    if (!name.trim()) {
      setError('Please enter your name.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.')
      return
    }
    if (!message.trim()) {
      setError('Please enter a message.')
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch(`${API_URL}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message }),
      })
      if (!res.ok) {
        const detail = await res.json().catch(() => null)
        throw new Error(
          detail?.detail ||
            (res.status === 429
              ? 'Too many submissions from this network. Try again in a minute.'
              : 'We could not send your note. Please email hello@pulsesignal.co directly.'),
        )
      }
      setSubmitted(true)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Network error.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="bg-cream-100 text-ink-900 overflow-hidden relative min-h-screen">
      <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-terra-100/40" />
        <div className="absolute top-[40%] right-[-200px] w-[600px] h-[600px] rounded-full blur-3xl bg-petrol-100/30" />
      </div>

      <MarketingNav />

      <section className="editorial relative pt-28 sm:pt-36 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left rail */}
          <aside className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-8">
              <span className="signal-dot" />
              <span className="mono text-caption uppercase tracking-widest text-ink-600">
                Contact
              </span>
            </div>
            <h1 className="text-h-xl font-semibold tracking-[-0.035em] leading-[0.98] text-ink-900 text-balance">
              Talk to a{' '}
              <span className="text-terra-500">human.</span>
            </h1>
            <p className="lede mt-6 text-pretty text-ink-700">
              Sales, partnerships, support, or a question we have not thought of.
              We answer every message, usually within the same business day.
            </p>

            <div className="mt-10 space-y-4">
              <ContactRow
                icon={Mail}
                label="General"
                value="hello@pulsesignal.co"
                href="mailto:hello@pulsesignal.co"
              />
              <ContactRow
                icon={Wallet}
                label="Sales"
                value="hello@pulsesignal.co"
                href="mailto:hello@pulsesignal.co"
              />
              <ContactRow
                icon={LifeBuoy}
                label="Support"
                value="support@pulsesignal.co"
                href="mailto:support@pulsesignal.co"
              />
              <ContactRow
                icon={ShieldCheck}
                label="Security"
                value="security@pulsesignal.co"
                href="mailto:security@pulsesignal.co"
              />
              <ContactRow
                icon={Mail}
                label="Privacy"
                value="privacy@pulsesignal.co"
                href="mailto:privacy@pulsesignal.co"
              />
              <ContactRow
                icon={Handshake}
                label="Partnerships"
                value="hello@pulsesignal.co"
                href="mailto:hello@pulsesignal.co"
              />
              <ContactRow
                icon={Newspaper}
                label="Press"
                value="press@pulsesignal.co"
                href="mailto:press@pulsesignal.co"
              />
            </div>

            <div className="rule mt-10" />
            <p className="mt-6 mono text-caption uppercase tracking-widest text-ink-600">
              Response time · same business day
            </p>
          </aside>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              noValidate
              className="bg-cream-50 border border-ink-900/10 rounded-2xl p-6 sm:p-8 lg:p-10"
            >
              {submitted ? (
                <div className="flex flex-col items-center text-center py-12 sm:py-16">
                  <div className="w-12 h-12 rounded-full bg-terra-500/15 flex items-center justify-center">
                    <Check className="w-6 h-6 text-terra-500" />
                  </div>
                  <h2 className="text-h-md font-semibold tracking-tight text-ink-900 mt-5">
                    Message received.
                  </h2>
                  <p className="lede mt-3 max-w-md text-ink-700">
                    We&apos;ll be in touch within one business day. While you wait, your
                    14-day trial is one click away.
                  </p>
                  <Link href="/signup" className="btn-primary mt-6">
                    Start 14-day trial
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <>
                  <p className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold">
                    Send a note
                  </p>
                  <h2 className="text-h-md font-semibold tracking-tight text-ink-900 mt-3">
                    What brings you here?
                  </h2>

                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name">
                      <input
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        type="text"
                        placeholder="Jane Doe"
                        aria-label="Name"
                        className="cream-input"
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="jane@company.com"
                        aria-label="Email"
                        className="cream-input"
                      />
                    </Field>
                  </div>

                  <Field label="Subject" className="mt-5">
                    <div className="flex flex-wrap gap-1 bg-cream-100 border border-ink-900/10 rounded-full p-1 w-fit">
                      {SUBJECTS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSubject(s)}
                          className={
                            'px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ' +
                            (s === subject
                              ? 'bg-terra-500 text-cream-50'
                              : 'text-ink-700 hover:text-ink-900')
                          }
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Message" className="mt-5">
                    <textarea
                      required
                      rows={6}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What are you trying to figure out?"
                      aria-label="Message"
                      className="cream-input resize-y"
                    />
                  </Field>

                  {error && (
                    <p
                      role="alert"
                      aria-live="assertive"
                      className="mt-5 text-body-sm text-maroon-600 bg-maroon-500/10 border border-maroon-500/30 rounded-lg px-3 py-2"
                    >
                      {error}
                    </p>
                  )}

                  <div className="mt-7 flex items-center justify-between flex-wrap gap-4">
                    <p className="mono text-caption text-ink-600">
                      We will not share your address with anyone.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? 'Sending...' : 'Send message'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      <MarketingFooter />

      {/* Light input style scoped to this page (mirrors btn-primary token system). */}
      <style>{`
        .cream-input {
          width: 100%;
          background: #fbf7ec;
          border: 1px solid rgba(11, 12, 16, 0.12);
          border-radius: 10px;
          padding: 10px 14px;
          color: #0b0c10;
          /* 16px floor prevents iOS Safari auto-zoom on focus. */
          font-size: 16px;
          transition: border-color .15s, box-shadow .15s;
        }
        .cream-input::placeholder { color: rgba(11,12,16,0.40); }
        .cream-input:focus {
          outline: none;
          border-color: rgba(199, 93, 44, 0.55);
          box-shadow: 0 0 0 3px rgba(199, 93, 44, 0.15);
        }
      `}</style>
    </main>
  )
}

function Field({
  label, children, className = '',
}: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={'block ' + className}>
      <span className="mono text-eyebrow uppercase tracking-widest text-ink-600 font-semibold block mb-2">
        {label}
      </span>
      {children}
    </label>
  )
}

function ContactRow({
  icon: Icon, label, value, href,
}: {
  icon: React.ElementType; label: string; value: string; href: string
}) {
  return (
    <a
      href={href}
      className="flex items-start gap-4 group"
      target={href.startsWith('http') ? '_blank' : undefined}
      rel="noreferrer"
    >
      <div className="w-9 h-9 rounded-lg border border-ink-900/15 flex items-center justify-center group-hover:border-terra-500/50 transition-colors">
        <Icon className="w-4 h-4 text-ink-700 group-hover:text-terra-500 transition-colors" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <p className="mono text-[11px] uppercase tracking-widest text-ink-600 font-semibold">
          {label}
        </p>
        <p className="text-body text-ink-900 mt-1 group-hover:text-terra-500 transition-colors">
          {value}
        </p>
      </div>
    </a>
  )
}
